/**
 * Sozlamalar' shell (spec §2): its own sidebar, beside the section it opens. The sidebar lists
 * only the sections the caller may open, so an owner sees seven and a branch manager two.
 *
 * `/sozlamalar` itself carries no content: it forwards to the first section the caller can open,
 * keeping the query string, because the Meta and Telegram callbacks land on `/sozlamalar?…`.
 */
import type { JSX } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router";

import { useAuthStore } from "@/store/auth";
import { t } from "@/i18n";
import { settingsNavFor } from "./settingsNav";

export function SettingsLayout(): JSX.Element {
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const items = settingsNavFor(role);

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      <nav aria-label={t("settings.layout.label")} className="md:w-56 md:shrink-0">
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `block rounded-control px-3 py-2 text-sm ${
                    isActive ? "bg-surface-muted font-medium text-ink" : "text-ink-muted hover:bg-surface-muted"
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

/** `/sozlamalar` → the first section the caller may open, query string kept. */
export function SettingsIndexRedirect(): JSX.Element {
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const location = useLocation();
  const first = settingsNavFor(role)[0];
  if (first === undefined) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={{ pathname: first.to, search: location.search }} replace />;
}
